import type { Novu } from '@novu/js';
import type { Ref, ShallowRef } from 'vue';

import { useRequestStateMachine } from '@peanuds/composables';

import type { AppNotification } from '../../types/notifications.types';
import { mapNotifications } from '../../utils/notifications.mappers';

interface UseNotificationsFetchModuleConfig {
  novu: Readonly<ShallowRef<Novu | null>>;
  notifications: Ref<AppNotification[]>;
  hasMore: Ref<boolean>;
  unreadCount: Ref<number>;
}

type NotificationsListResponse = Awaited<ReturnType<Novu['notifications']['list']>>;

export const useNotificationsFetchModule = (config: UseNotificationsFetchModuleConfig) => {
  const clearNotifications = () => {
    config.notifications.value = [];
    config.hasMore.value = false;
  };

  const fetchNotificationsRequest = async () => {
    if (!config.novu.value) {
      return undefined;
    }

    return config.novu.value.notifications.list({
      limit: 100,
      useCache: false,
    });
  };

  const {
    fetchData: runFetchNotifications,
    isLoading: isLoadingNotifications,
    error: notificationsError,
  } = useRequestStateMachine<
    NotificationsListResponse | undefined,
    typeof fetchNotificationsRequest
  >({
    request: fetchNotificationsRequest,
  });

  const fetchUnreadCount = async () => {
    const client = config.novu.value;
    if (!client) {
      return;
    }

    try {
      const { data, error } = await client.notifications.count({
        read: false,
      });

      if (config.novu.value !== client || error || !data) {
        return;
      }

      config.unreadCount.value = data.count;
    } catch (error) {
      console.error('[NotificationsStore] Failed to fetch unread count:', error);
    }
  };

  let inFlightClient: Novu | null = null;

  const fetchNotifications = async () => {
    const client = config.novu.value;
    if (!client) {
      return;
    }

    if (isLoadingNotifications.value && inFlightClient === client) {
      return;
    }

    inFlightClient = client;
    const response = await runFetchNotifications();
    if (config.novu.value !== client) {
      return;
    }

    if (!response) {
      clearNotifications();
      return;
    }

    const { data, error } = response;
    if (error) {
      console.error('[NotificationsStore] Novu list error:', error);
      clearNotifications();
      return;
    }

    if (!data) {
      clearNotifications();
      return;
    }

    config.notifications.value = mapNotifications(data.notifications);
    config.hasMore.value = data.hasMore;
  };

  return {
    fetchNotifications,
    fetchUnreadCount,
    isLoadingNotifications,
    notificationsError,
  };
};
